import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";

const ReportPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Report Policy"
        description="CSW Report Policy. Learn how to report inappropriate content or behavior in our virtual study rooms and how we handle reports."
        keywords="csw report policy, report user, study room safety"
        canonicalUrl="/report-policy/"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
              <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                Report Policy
              </span>
            </h1>
            <p className="text-lg text-muted-foreground">
              CSW – Chill Study Work App
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-card/50 backdrop-blur-sm border border-border rounded-2xl p-8 md:p-12">
            <div className="prose prose-lg dark:prose-invert max-w-none">
              
              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">1. Introduction</h2>
                <p className="text-muted-foreground leading-relaxed">
                  At CSW – Chill Study Work App ("CSW," "we," "our," or "us"), we strive to create a safe and positive environment for all users. To maintain this standard, we encourage our users to report any content or behavior that violates our community guidelines, terms of service, or legal requirements. This Report Policy outlines the process for reporting issues and how we handle such reports.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">2. What Can Be Reported</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">Users are encouraged to report the following types of content or behavior:</p>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Inappropriate Content:</strong> Any content that violates our content guidelines, including hate speech, harassment, violence, explicit sexual content, illegal activities, or misinformation.</p>
                  <p><strong className="text-foreground">Abusive Behavior:</strong> Harassment, bullying, threats, or any other abusive behavior targeted at individuals or groups.</p>
                  <p><strong className="text-foreground">Spam or Scams:</strong> Content or accounts that engage in spam, phishing, or fraudulent activities.</p>
                  <p><strong className="text-foreground">Security Issues:</strong> Any vulnerabilities, unauthorized access, or suspicious activities that may threaten the security of the platform or its users.</p>
                  <p><strong className="text-foreground">Impersonation:</strong> Accounts that impersonate other individuals, organizations, or entities.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">3. How to Report</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">Users can report content or behavior through the following methods:</p>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">In-App Reporting:</strong> Users can report content directly within the app by using the "Report" button associated with the content or user in question. This option is available for messages, posts, profiles, and other forms of content.</p>
                  <p><strong className="text-foreground">Contacting Support:</strong> Users can also report issues by contacting our support team via email at info@csw.live or through the "Contact Us" section within the app. Please include details of the issue, including screenshots or links if applicable.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">4. Handling of Reports</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">Once a report is submitted, the following steps are taken:</p>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Acknowledgment:</strong> The user who submitted the report will receive an acknowledgment that their report has been received and is being reviewed.</p>
                  <p><strong className="text-foreground">Review Process:</strong> Our moderation team will thoroughly review the reported content or behavior. This process may involve automated tools, manual review, or a combination of both, depending on the nature of the report.</p>
                  <p><strong className="text-foreground">Action Taken:</strong> Based on the outcome of the review, appropriate action will be taken. This may include content removal, issuing warnings, suspending or terminating accounts, or reporting to relevant authorities in the case of illegal activities.</p>
                  <p><strong className="text-foreground">Notification:</strong> Users who submit reports may receive a notification about the outcome of their report, depending on the severity and nature of the issue.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">5. Confidentiality and Privacy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We respect the confidentiality and privacy of users who submit reports. The identity of the reporting user will not be disclosed to the person or entity being reported, except in cases where disclosure is required by law.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">6. Misuse of Reporting System</h2>
                <p className="text-muted-foreground leading-relaxed">
                  The reporting system is intended to protect the community and ensure a safe environment. Misuse of the reporting system, such as submitting false or malicious reports, may result in disciplinary action, including warnings, suspension, or termination of the user's account.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">7. Appeals</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If a user believes that their content or account was wrongfully flagged or penalized, they may appeal the decision by contacting our support team at info@csw.live. Appeals will be reviewed, and a final decision will be communicated to the user.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">8. Updates to This Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Report Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We encourage users to review this policy periodically.
                </p>
              </div>

              <div className="mb-0">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">9. Contact Information</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  If you have any questions or concerns about this Report Policy, please contact us at:
                </p>
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
                  <p className="font-semibold text-foreground mb-2">CSW – Chill Study Work App</p>
                  <a href="mailto:info@csw.live" className="text-primary hover:text-primary/80 transition-colors">
                    info@csw.live
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReportPolicy;
